import cn from 'classnames';
import { LinkButton } from '#components/atoms/buttons/link-button/LinkButton.tsx';
import { DetailCard } from '#components/atoms/cards/detail-card/DetailCard.tsx';
import { useAuth } from '#global/context/auth/useAuth.tsx';
import { PRO_PLAN_FEATURES, PRO_PRICE_PER_HOST_USD } from '../constants.ts';
import { FeatureList } from './FeatureList.tsx';
import styles from './ProPlanCard.module.css';

const countFormat = new Intl.NumberFormat();
const currencyFormat = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
  trailingZeroDisplay: 'stripIfInteger',
});

type Props = {
  /** Current enrolled host count (includes lighthouses and relays) */
  currentHostCount: number;
};

export function ProPlanCard({ currentHostCount }: Props) {
  const userType = useAuth().user?.type;

  if (!userType) return null;

  return (
    <DetailCard className={styles.ProPlanCard}>
      <h2 className="h4 mb-0">Get more with Pro</h2>
      {currentHostCount > 0 ?
        <div>
          <div className="flex gap-8 align-center">
            <span className="h1 mb-0">{currencyFormat.format(currentHostCount * PRO_PRICE_PER_HOST_USD)}</span>
            <span className="h6 mb-0 nowrap">/mo</span>
          </div>
          <p className={cn('p-sm mb-0', styles.ProPlanCard_priceCaption)}>
            {`Based on your current host count (${countFormat.format(currentHostCount)}) and ${currencyFormat.format(
              PRO_PRICE_PER_HOST_USD
            )}/mo per host`}
          </p>
        </div>
      : <div className="flex gap-8 align-center">
          <span className="h1 mb-0">{currencyFormat.format(PRO_PRICE_PER_HOST_USD)}</span>
          <span className="h6 mb-0 nowrap">/mo per host</span>
        </div>
      }
      <div>
        <p className={cn('eyebrow', styles.ProPlanCard_featuresLabel)}>Everything from Free and:</p>
        <FeatureList features={PRO_PLAN_FEATURES} />
      </div>
      <div className={styles.ProPlanCard_footer}>
        {userType === 'user' ?
          <>
            <LinkButton to="/settings/billing/checkout" variant="primary" fullWidth={true}>
              Upgrade to Pro
            </LinkButton>
            <p className={cn('p-sm mb-0', styles.ProPlanCard_byline)}>
              Prorated monthly billing · Cancel anytime · Managed by Stripe
            </p>
          </>
        : <p className="p-md mb-0">
            To upgrade to the Pro plan,
            <br />
            please log in without single sign-on
          </p>
        }
      </div>
    </DetailCard>
  );
}
